import { useMemo } from "react";

// --- Constants (Can be adjusted or made configurable via props) ---
const COLUMN_MIN_WIDTH = 400; // Minimum width for a column in pixels
const ROW_HEIGHT = 300; // Approximate height of a ComponentCard (adjust based on actual card size)
const GAP = 20; // Gap between grid items in pixels
const SCROLLBAR_WIDTH = 17; // Estimated width of the vertical scrollbar

// Define the expected structure for bounds inline
interface Bounds {
	left: number;
	top: number;
	width: number;
	height: number;
	x?: number; // Optional, depending on react-use-measure version/config
	y?: number; // Optional
	right?: number; // Optional
	bottom?: number; // Optional
}

interface UseGridCalculationsProps<T> {
	bounds: Bounds; // Use the inline defined Bounds type
	items: T[]; // Generic list of items to calculate grid for
}

export function useGridCalculations<T>({ bounds, items }: UseGridCalculationsProps<T>) {
	/**
	 * Memoized calculation of grid dimensions based on container bounds and item count.
	 * Ensures calculations are only re-run when width or item count changes.
	 */
	const calculations = useMemo(() => {
		const gridWidth = bounds.width;
		const availableGridWidth =
			gridWidth > SCROLLBAR_WIDTH ? gridWidth - SCROLLBAR_WIDTH : gridWidth;

		const columnCount =
			availableGridWidth > 0
				? Math.max(
						1,
						Math.floor((availableGridWidth + GAP) / (COLUMN_MIN_WIDTH + GAP)),
					)
				: 1;

		const actualColumnWidth = COLUMN_MIN_WIDTH;

		const rowCount = Math.ceil(items.length / columnCount);

		const totalGridHeight = rowCount * (ROW_HEIGHT + GAP);

		const totalGridWidth = columnCount * (actualColumnWidth + GAP) - GAP;

		return {
			columnCount,
			actualColumnWidth,
			rowCount,
			totalGridHeight,
			totalGridWidth,
			GAP,
			ROW_HEIGHT,
		};
	}, [bounds.width, items.length]); // Dependencies: only re-calculate if width or number of items changes

	return calculations;
}
